/**
 * TanStackTableWrapper - Complete replacement for react-table with identical API
 * Maintains 100% backward compatibility while using modern TanStack Table v8
 */
/* eslint-disable */
import * as React from "react";
import { ReactReduxContext } from "react-redux";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  ColumnFiltersState,
  SortingState,
  PaginationState,
  ColumnResizeMode,
} from "@tanstack/react-table";
import classNames from "classnames";
import Spinner from "../../Spinner/Spinner";
import CheckedMark from "../../CheckedMark/CheckedMark";
import { ColumnAdapter, LegacyFilter } from "./ColumnAdapter";
import { StateAdapter } from "./StateAdapter";
import { EventAdapter } from "./EventAdapter";
import { PaginationAdapter } from "./PaginationAdapter";
import { TableProps } from "../Table";
import styles from "../Table.module.scss";
// Using original react-table.css globally imported in index.tsx

// Re-export types for backward compatibility
export type { LegacyFilter as Filter } from "./ColumnAdapter";

const TanStackTableWrapper: React.FunctionComponent<TableProps> = ({
    rows,
    columns,
    handleRowSelection,
    getOption,
    isRowSelected,
    isHidingTextActive,
    pageSize,
    disablePagination,
    setHeight,
    startingPage,
    loading,
    loadingText,
    handleFilterChange,
    filtered,
    // Legacy props for backward compatibility
    style,
    className,
    getTdProps,
    getTrProps,
    filterable,
    showPagination,
    showPageSizeOptions,
    defaultFilterMethod,
    data,
    minRows,
    ...otherProps
  }) => {
    // Use data prop if provided (legacy), otherwise use rows
    const tableData = data || rows;

    // Read pageSize directly from Redux store with forced subscription
    const { store } = React.useContext(ReactReduxContext);
    const [effectivePageSize, setEffectivePageSize] = React.useState<number>(
      store.getState().main.pageSize || pageSize
    );
    React.useEffect(() => {
      const unsubscribe = store.subscribe(() => {
        const newPageSize = store.getState().main.pageSize;
        setEffectivePageSize(prev => prev !== newPageSize ? newPageSize : prev);
      });
      return unsubscribe;
    }, [store]);
    // Convert legacy columns to TanStack columns using adapter
    const tanstackColumns = React.useMemo(() => {
      // Process columns to generate dropdown options from data for dropdown filtering
      const processedColumns = columns.map(column => {
        if (column.hasDropdownFiltering && !column.filterDropdownOptions) {
          let uniqueValues: (string | number)[];
          
          if ((column as any).isMultiValueDropdown) {
            // For multi-value columns, split comma-separated values into individual options
            const allValues: string[] = [];
            tableData.forEach(row => {
              const cellValue = column.ownAccessor(row);
              if (cellValue && typeof cellValue === 'string') {
                cellValue.split(',').forEach((val: string) => {
                  const trimmed = val.trim();
                  if (trimmed) allValues.push(trimmed);
                });
              }
            });
            uniqueValues = Array.from(new Set(allValues)).sort();
          } else {
            // Auto-generate dropdown options from unique values in data
            uniqueValues = Array.from(new Set(
              tableData.map(row => column.ownAccessor(row))
                .filter(value => value !== null && value !== undefined && value !== "")
            )).sort();
          }
          
          return {
            ...column,
            filterDropdownOptions: uniqueValues
          };
        }
        return column;
      });
      
      return ColumnAdapter.mapColumns(processedColumns);
    }, [columns, tableData]);

    // State management using adapters
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
      StateAdapter.createInitialFilters(filtered)
    );
    const [sorting, setSorting] = React.useState<SortingState>(
      StateAdapter.createInitialSorting()
    );
    const [pagination, setPagination] = React.useState<PaginationState>(
      StateAdapter.createInitialPagination(effectivePageSize, startingPage)
    );

    // Sync with legacy filtered prop using adapter
    React.useEffect(() => {
      StateAdapter.syncFilters(filtered, columnFilters, setColumnFilters);
    }, [filtered]);

    // Sync pageSize with internal pagination state (reads from Redux directly)
    React.useEffect(() => {
      setPagination(prev => {
        if (prev.pageSize !== effectivePageSize) {
          return { pageIndex: 0, pageSize: effectivePageSize };
        }
        return prev;
      });
    }, [effectivePageSize]);

    // Handle filter changes using event adapter
    const handleFiltersChange = React.useCallback(
      EventAdapter.mapFilterChangeHandler(
        handleFilterChange,
        () => StateAdapter.resetPaginationOnFilter(setPagination)
      ),
      [handleFilterChange]
    );

    // Handle pagination changes using event adapter
    const handlePaginationChange = React.useCallback(
      EventAdapter.mapPageChangeHandler(undefined),
      []
    );

    // Initialize TanStack table with all features
    const table = useReactTable({
      data: tableData,
      columns: tanstackColumns,
      getRowId: (row, index) => row.id != null ? String(row.id) : row.enumValue != null ? String(row.enumValue) : String(index),
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getPaginationRowModel: disablePagination ? undefined : getPaginationRowModel(),
      onColumnFiltersChange: (updater) => {
        const newFilters =
          typeof updater === "function" ? updater(columnFilters) : updater;
        setColumnFilters(newFilters);
        handleFiltersChange(newFilters);
      },
      onSortingChange: setSorting,
      onPaginationChange: (updater) => {
        const newPagination =
          typeof updater === "function" ? updater(pagination)
          : updater;
        setPagination(newPagination);
        handlePaginationChange(newPagination);
      },
      globalFilterFn: ColumnAdapter.createFuzzyFilter(),
      filterFns: {
        fuzzy: ColumnAdapter.createFuzzyFilter(),
      },
      defaultColumn: {
        filterFn: ColumnAdapter.createDefaultFilter(),
      },
      state: {
        columnFilters,
        sorting,
        pagination,
      },
      manualPagination: false,
      autoResetPageIndex: false,
      enableFilters: filterable !== false,
      columnResizeMode: 'onChange' as ColumnResizeMode,
      enableColumnResizing: true,
    });

    // Event handlers using adapters
    const rowClickHandler = React.useCallback(
      EventAdapter.createRowClickHandler(handleRowSelection, getOption),
      [handleRowSelection, getOption]
    );

    const isRowSelectedChecker = React.useCallback(
      EventAdapter.createRowSelectedChecker(isRowSelected),
      [isRowSelected]
    );

    const showLoading = loading ? tableData.length === 0 : false;
    const shouldHideHeaders = columns.every(col => 
      (col as any).Header?.toString() === "() => null"
    );

    return (
      <div className={styles.container} style={{ height: setHeight }}>
        <div className={classNames("ReactTable -striped -highlight", className)} style={{ ...style, display: 'flex', flexDirection: 'column', overflowX: 'auto', overflowY: 'hidden' }}>
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', flex: 1, minWidth: 'max-content', overflow: 'auto' }}>
            {/* Loading overlay matching original design */}
            {showLoading && (
              <div className="rt-loading -active">
                <div className="rt-loading-text">
                  <Spinner loading={true} loadingText="" />
                  <div className={styles.loaderPosition}>
                  {loadingText}
                  </div>
                </div>
              </div>
            )}

            {/* Fixed Header section - outside scrollable area */}
            {!shouldHideHeaders && (
              <div className="rt-table" role="grid" style={{ overflow: 'visible', flex: 'none' }}>
                {/* Main header row */}
                <div className="rt-thead -header">
                  {table.getHeaderGroups().map(headerGroup => (
                    <div key={headerGroup.id} className="rt-tr" role="row">
                      {headerGroup.headers.map(header => {
                        const meta = header.column.columnDef.meta as any;
                        return (
                          <div
                            key={header.id}
                            className={classNames("rt-th rt-resizable-header", meta?.className, {
                              "-cursor-pointer": header.column.getCanSort()
                            })}
                            role="columnheader"
                            tabIndex={-1}
                            style={{ 
                              flex: meta?.isCheckboxColumn ? '30 0 auto' : `${header.getSize()} 0 auto`,
                              width: meta?.isCheckboxColumn ? 30 : header.getSize(),
                              minWidth: meta?.isCheckboxColumn ? 30 : header.column.columnDef.minSize,
                              position: 'relative',
                              ...meta?.style,
                            }}
                          >
                            <div className="rt-resizable-header-content">
                              {header.isPlaceholder ? null : (
                                <div
                                  className="rt-th-content"
                                  onClick={header.column.getCanSort() ? 
                                    header.column.getToggleSortingHandler() : 
                                    undefined
                                  }
                                >
                                  {flexRender(header.column.columnDef.header, header.getContext())}
                                  {/* Sort indicators */}
                                  {header.column.getIsSorted() === 'asc' && <span style={{ color: '#0071B9', marginLeft: '4px', fontSize: '10px' }}>▲</span>}
                                  {header.column.getIsSorted() === 'desc' && <span style={{ color: '#0071B9', marginLeft: '4px', fontSize: '10px' }}>▼</span>}
                                </div>
                              )}
                            </div>
                            {/* Column resize handle */}
                            <div
                              onMouseDown={header.getResizeHandler()}
                              onTouchStart={header.getResizeHandler()}
                              className={`rt-resizer ${header.column.getIsResizing() ? 'isResizing' : ''}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>

                {/* Separate filter row matching original structure */}
                {filterable !== false && <div className="rt-thead -filters">
                  <div className="rt-tr" role="row">
                    {table.getHeaderGroups()[0].headers.map(header => {
                      const meta = header.column.columnDef.meta as any;
                      return (
                        <div
                          key={`filter-${header.id}`}
                          className={classNames("rt-th", meta?.className)}
                          style={{ 
                            flex: meta?.isCheckboxColumn ? '30 0 auto' : `${header.getSize()} 0 auto`,
                            width: meta?.isCheckboxColumn ? 30 : header.getSize(),
                            minWidth: meta?.isCheckboxColumn ? 30 : header.column.columnDef.minSize,
                            position: 'relative',
                            ...meta?.style,
                          }}
                        >
                          {header.column.getCanFilter() && (
                            meta?.filterComponent ? (
                              React.createElement(meta.filterComponent, { column: header.column })
                            ) : (
                              <input
                                type="text"
                                name={header.id}
                                value={(header.column.getFilterValue() ?? "") as string}
                                onChange={(e) => header.column.setFilterValue(e.target.value)}
                                placeholder=""
                                style={{
                                  width: "calc(100% - 2px)",
                                  paddingRight: "5px"
                                }}
                              />
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>}
              </div>
            )}

            {/* Scrollable Body section */}
            <div className="rt-table" role="grid" style={{ flex: 1, overflowY: 'auto', overflowX: 'auto' }}>
              <div className={classNames("rt-tbody", {
                [styles.bottomMarginWithoutPaging]: disablePagination
              })}>
              {table.getRowModel().rows.length === 0 && !showLoading ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", minHeight: "300px", width: "100%", color: "#666" }}>
                  {"No rows found"}
                </div>
              ) : (
                table.getRowModel().rows.map((row, index) => (
                  <div key={row.id} className="rt-tr-group" role="rowgroup">
                    <div
                      className={classNames("rt-tr", 
                        index % 2 === 0 ? "-even" : "-odd",
                        {
                          [styles.selectedRow]: isRowSelectedChecker(row)
                        }
                      )}
                      role="row"
                    >
                      {row.getVisibleCells().map(cell => {
                        const meta = cell.column.columnDef.meta as any;
                        return (
                          <div
                            key={cell.id}
                            className={classNames("rt-td", {
                              [styles.row]: !isHidingTextActive,
                              [styles.selectableRow]: true
                            })}
                            style={{
                              flex: meta?.isCheckboxColumn ? '30 0 auto' : `${cell.column.getSize()} 0 auto`,
                              width: meta?.isCheckboxColumn ? 30 : cell.column.getSize(),
                              minWidth: meta?.isCheckboxColumn ? 30 : cell.column.columnDef.minSize,
                              position: 'relative',
                              ...meta?.style,
                            }}
                            onClick={(e) => rowClickHandler(e, row)}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
            </div>
          </div>

          {/* Pagination section - inside ReactTable for proper CSS styling */}
          {!disablePagination && (
            <div className="pagination-bottom">
              <PaginationAdapter 
                table={table} 
                onPageChange={(page) => handlePaginationChange({ pageIndex: page, pageSize: effectivePageSize })} 
              />
            </div>
          )}
        </div>
      </div>
    );
  };

// Set display name for debugging
TanStackTableWrapper.displayName = "TanStackTableWrapper";

export default TanStackTableWrapper;